/**
 * SubscriptionContext
 *
 * Shares the current user's subscription tier and usage data across the app.
 * Fetches from GET /api/account/usage with polling, so child components
 * (TrialBanner, FeatureGate, UsageWidget, etc.) don't each make their own
 * requests.
 */

import type { AccountUsageResponse } from "@/lib/api/routes/account-usage";
import * as React from "react";

const POLL_INTERVAL_MS = 60_000;

/**
 * The ordered hierarchy of subscription tiers (lowest to highest).
 * Includes both legacy Stripe tier names and new Polar tier names
 * for backward compatibility with existing users.
 */
const TIER_RANK: Record<string, number> = {
  trial: 0,
  starter: 1,              // legacy Stripe tier (same access as small)
  small: 1,                // new Polar tier
  professional: 2,         // legacy Stripe tier (same access as unlimited)
  unlimited: 2,            // new Polar tier
  enterprise: 3,
  legacy_professional: 3,  // legacy users get professional-level access
};

export type SubscriptionContextValue = {
  /** Raw usage data from /api/account/usage, null while loading. */
  data: AccountUsageResponse | null;
  /** Whether we're still waiting for the first successful fetch. */
  loading: boolean;
  /** Whether the last fetch errored. */
  error: boolean;
  /** Refresh usage data on demand (e.g. after performing an action). */
  refresh: () => void;
  /**
   * Returns true if the user's current tier meets or exceeds the required tier.
   * Always returns true while data is loading (avoid blocking UI while fetching).
   */
  hasTier: (requiredTier: string) => boolean;
};

const SubscriptionContext = React.createContext<SubscriptionContextValue | null>(null);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const [data, setData] = React.useState<AccountUsageResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  const fetchUsage = React.useCallback(async () => {
    try {
      const res = await fetch("/api/account/usage", { credentials: "include" });
      if (!res.ok) {
        setError(true);
        return;
      }
      const json = await res.json();
      if (json.status === "success") {
        setData(json.data);
        setError(false);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void fetchUsage();

    if (typeof document === "undefined") {
      return undefined;
    }

    const pollIfVisible = () => {
      if (document.visibilityState === "visible") {
        void fetchUsage();
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void fetchUsage();
      }
    };

    const timer = setInterval(pollIfVisible, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [fetchUsage]);

  const hasTier = React.useCallback(
    (requiredTier: string): boolean => {
      if (!data) return true; // don't block UI while loading
      const currentRank = TIER_RANK[data.subscription_tier] ?? -1;
      const requiredRank = TIER_RANK[requiredTier] ?? 999;
      return currentRank >= requiredRank;
    },
    [data],
  );

  const value = React.useMemo<SubscriptionContextValue>(
    () => ({ data, loading, error, refresh: fetchUsage, hasTier }),
    [data, loading, error, fetchUsage, hasTier],
  );

  return <SubscriptionContext value={value}>{children}</SubscriptionContext>;
}

/**
 * Hook to access subscription data from any component in the tree.
 *
 * Must be called inside a <SubscriptionProvider>.
 */
export function useSubscription(): SubscriptionContextValue {
  const ctx = React.useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error("useSubscription must be used within a <SubscriptionProvider>");
  }
  return ctx;
}
